<template>
    <UCard
        id="login-card"
        class="flex flex-1 flex-col"
        :ui="{
            background: 'bg-white dark:bg-gray-800',
            divide: 'divide-y divide-gray-100 dark:divide-gray-800',
            ring: '',
            body: { base: 'flex-1' },
            header: {
                background: 'py-[29px] shadow-md bg-blue-600 dark:bg-gray-900'
            }
        }">
        <template #header>
            <div class="flex items-center justify-between">
                <h3 class="text-lg leading-6 text-white">Client Login</h3>

                <UButton color="white" variant="outline" size="sm" icon="i-bi-x" class="-my-1 text-white" @click="toggleLogin" />
            </div>
        </template>

        <UCard>
            <div class="mb-4 text-center">
                <UIcon name="i-bi-person-lock" class="text-primary-500 h-16 w-16 dark:text-white" />

                <h2 class="text-2xl font-bold text-blue-600 dark:text-white">Welcome back!</h2>

                <p class="mt-1 text-sm text-dark-800 dark:text-gray-400">
                    If you do not have an account then one will be created after your first purchase.
                </p>
            </div>

            <UForm :schema="schema" :state="input" @submit="handleLoginForm">
                <UFormGroup class="mb-6" label="Email Address" name="email" id="email" eager-validation>
                    <UInput
                        type="email"
                        input-class="lowercase"
                        icon="i-bi-envelope-at"
                        size="lg"
                        color="white"
                        variant="outline"
                        v-model="input.email"
                        :disabled="disabled" />
                </UFormGroup>

                <UFormGroup class="relative mb-6" label="Password" name="password" id="password" eager-validation>
                    <UInput
                        :type="isShowingPassword ? 'text' : 'password'"
                        icon="i-bi-person-lock"
                        size="lg"
                        color="white"
                        variant="outline"
                        v-model="input.password"
                        :disabled="disabled" />

                    <NuxtLink
                        class="absolute -top-[22px] right-0 cursor-pointer text-xs font-semibold text-dark-700 underline decoration-dashed underline-offset-2 hover:text-dark-950 hover:no-underline"
                        @click="forgottenPassword">
                        Forgot password?
                    </NuxtLink>

                    <UIcon
                        :name="isShowingPassword ? 'i-bi-eye' : 'i-bi-eye-slash'"
                        class="absolute right-3 top-3 h-4 w-4 cursor-pointer text-dark-500 dark:text-white"
                        @click="togglePassword" />
                </UFormGroup>

                <NuxtTurnstile class="my-4 w-[100%]" v-model="token" :options="{ theme: 'light', size: 'flexible' }" />

                <UButton
                    type="submit"
                    class="uppercase"
                    :icon="loading ? '' : 'i-bi-person-lock'"
                    size="xl"
                    color="success"
                    variant="solid"
                    :label="loading ? 'Processing...' : 'Sign In'"
                    block
                    :disabled="disabled"
                    :loading="loading" />
            </UForm>

            <div class="mt-4 text-center">
                <p class="text-center text-xs text-gray-500 dark:text-gray-400">
                    By signing in, you agree to our
                    <NuxtLink
                        to="/"
                        class="cursor-pointer font-semibold text-dark-700 underline decoration-dashed underline-offset-2 hover:text-dark-950 hover:no-underline"
                        @click="toggleLogin"
                        >Terms of Use</NuxtLink
                    >
                    and
                    <NuxtLink
                        to="/"
                        class="cursor-pointer font-semibold text-dark-700 underline decoration-dashed underline-offset-2 hover:text-dark-950 hover:no-underline"
                        @click="toggleLogin"
                        >Privacy Policy</NuxtLink
                    >.
                </p>
            </div>
        </UCard>
    </UCard>
</template>

<script setup>
    import { z } from 'zod'

    const { toggleLogin, loading, disabled } = inject('login')
    const { user, signInWithEmail } = useAuthentication()
    const { fetchUserRole, fetchUserRolePermissions, fetchUserDetails } = useUser()
    const userStore = useUserStore()
    const { timeout, capitalize, lowercase } = useHelpers()
    const { showSuccessMessage, showErrorMessage } = useMessages()

    const getUserRole = ref({ data: [], status: 'pending' })
    const getUserRolePermissions = ref({ data: [], status: 'pending' })
    const getUserDetails = ref({ data: [], status: 'pending' })
    const token = ref('')

    const forgottenPassword = () => {
        document.getElementById('login-card').classList.add('hidden')
        document.getElementById('forgot-password-card').classList.remove('hidden')
    }

    const isShowingPassword = ref(false)

    const togglePassword = () => {
        isShowingPassword.value = !isShowingPassword.value
    }

    const input = reactive({
        email: '',
        password: ''
    })

    const schema = z.object({
        email: z.string().nonempty('Email address is required.').email('Please provide a valid email address.'),
        password: z.string().nonempty('Password is required.').min(6, 'Please provide a valid password')
    })

    const showLoading = () => {
        loading.value = true
    }

    const clearInput = () => {
        input.email = ''
        input.password = ''
    }

    const clearLoading = () => {
        loading.value = false
    }

    const handleLoginForm = async () => {
        try {
            showLoading()

            await signInWithEmail({
                email: lowercase(input.email).trim(),
                password: input.password.trim(),
                captchaToken: token.value
            })

            if (user.value) {
                getUserRole.value = await fetchUserRole()
                getUserRolePermissions.value = await fetchUserRolePermissions(getUserRole.value.data.users_roles[0].roles.id)
                getUserDetails.value = await fetchUserDetails()

                const fullName = computed(() => {
                    return `${getUserDetails.value.data.users_admins.first_name} ${getUserDetails.value.data.users_admins.last_name}`
                })

                userStore.token = user.value.id
                userStore.role = getUserDetails.value.data.users_roles[0].roles.slug
                userStore.permissions = getUserRolePermissions.value.data.role_permissions[0]
                userStore.name = fullName.value
                userStore.email = getUserDetails.value.data.users_admins.email_address ?? null

                if (userStore.role !== 'member') {
                    await navigateTo('/authenticated/admin/dashboard')

                    clearInput()
                    clearLoading()
                    toggleLogin()

                    showSuccessMessage('Success!', 'You have signed in successfully.', timeout())
                } else {
                    await navigateTo('/authenticated/member/dashboard')

                    clearInput()
                    clearLoading()
                    toggleLogin()

                    showSuccessMessage('Congratulations!', 'You have signed in successfully.', timeout())
                }
            }
        } catch (error) {
            console.error('DEBUG: Handle Login Form Error: ', error.message)
            clearInput()
            clearLoading()

            showErrorMessage('Error!', capitalize(error.message))
        }
    }
</script>
